import { z } from 'zod';
import { EntityRef } from '../graph/entity-ref.js';
/**
 * Memory feed — the wire shape for `GET /api/orgs/:o/memory/feed`.
 *
 * The feed is the agent's transparency surface at `/memory`: every
 * "what changed in the graph this week" event the CEO can audit. It
 * categorizes generic agent audit events into the closed set of memory
 * kinds the designer reference (`assets/agentic.jsx` §6 MemoryFeed)
 * uses for the eyebrow tag column.
 *
 * The categorization happens server-side in
 * `apps/functions/src/synthesis/memory-categorize.ts` — so the client
 * doesn't have to know the audit-event taxonomy. Adding a new
 * memory-event kind here is the first step; the categorizer then needs
 * a clause mapping the underlying audit shape into it.
 */
/**
 * Closed set of memory-feed event categories. Each kind corresponds to
 * one row treatment in the `<MemoryFeedRow>` component:
 *   new-fact          → the agent recorded a new attribute about an entity
 *   new-entity        → the agent resolved a new entity into the graph
 *   new-connection    → the agent inferred a new edge between entities
 *   unlearned         → a CEO correction caused the agent to drop a prior fact
 *   confidence-up     → the agent revised its confidence in an existing fact upward
 *   confidence-down   → the agent revised its confidence downward
 *   watch             → the agent set up active monitoring on an entity / metric
 */
export const MemoryEventKind = z.enum([
    'new-fact',
    'new-entity',
    'new-connection',
    'unlearned',
    'confidence-up',
    'confidence-down',
    'watch',
]);
/**
 * Source channel that produced this memory event. Distinct from the
 * `FactSource` enum because the memory feed lives one level above raw
 * fact provenance:
 *   connector       — derived from a connector ingest (Slack, Gmail, …)
 *   dossier         — surfaced from a dossier the agent maintains
 *   correction      — a CEO correction triggered the change (unlearned, retract)
 *   agent-inference — pure LLM synthesis with no specific external source
 */
export const MemoryEventSourceKind = z.enum([
    'connector',
    'dossier',
    'correction',
    'agent-inference',
]);
/**
 * Entity reference embedded in a memory event — drives inline
 * `<EntityName>` rendering.
 *
 * Re-exported as an alias of the shared {@link EntityRef} (lives in
 * `packages/shared/src/graph/entity-ref.ts`). Kept as a named export so
 * existing call sites (`apps/web/src/components/memory/memory-feed-row.tsx`)
 * keep importing the memory-flavored name; new code should prefer
 * `EntityRef` directly.
 */
export const MemoryEventEntityRef = EntityRef;
/** One row in the memory feed. */
export const MemoryFeedEvent = z.object({
    /** Stable id — `${audit-doc-id}` or `${synthesizer}-${cursor}`. */
    id: z.string().min(1),
    kind: MemoryEventKind,
    /** 1-line headline; renders in the row title slot. */
    title: z.string().min(1).max(200),
    /** 2-3 line body copy. */
    body: z.string().max(800),
    /** ISO timestamp the event was recorded. */
    at: z.string(),
    /** Provenance for the underlying fact — drives the `<SourceBadge>` row. */
    source: z.object({
        kind: MemoryEventSourceKind,
        /** Pointer back into the underlying record — audit doc id, fact id, dossier slug. */
        ref: z.string().min(1),
        /** Human label for the source ("Slack #sales-eu", "Acme dossier", "CEO correction"). */
        label: z.string().min(1).max(200),
    }),
    /** 0..1 model confidence — drives the `<ConfidenceMeter>` when present. */
    confidence: z.number().min(0).max(1).optional(),
    /** Entities mentioned in this row — drives inline `<EntityName>` chips. */
    entities: z.array(EntityRef).default([]),
});
/** Tally section — the six serif numerals in the left rail of `/memory`. */
export const MemoryFeedTally = z.object({
    newFacts: z.number().int().min(0),
    newEntities: z.number().int().min(0),
    newConnections: z.number().int().min(0),
    unlearned: z.number().int().min(0),
    confidenceRaised: z.number().int().min(0),
    confidenceLowered: z.number().int().min(0),
});
/** Wire shape returned by `GET /api/orgs/:o/memory/feed`. */
export const MemoryFeedResponse = z.object({
    events: z.array(MemoryFeedEvent),
    tally: MemoryFeedTally,
    /** Inclusive window the feed covers — drives the page sub-copy. */
    window: z.object({
        from: z.string(),
        to: z.string(),
    }),
});
